import { useFetchPatientReferral } from '@/api/hooks/useFetchPatientReferral'
import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { analytics } from '@/lib/analytics'
import { formatCents } from '@/lib/money'
import { Button, ButtonText } from '@/ui/core/button'
import { Header } from '@/ui/core/header'
import { Ledger, numbered } from '@/ui/core/ledger'
import { Sheet, SheetTitle } from '@/ui/core/sheet'
import { Skeleton } from '@/ui/core/skeleton'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import * as Clipboard from 'expo-clipboard'
import { useState } from 'react'
import { Platform, Share, View } from 'react-native'
import Svg, { Line } from 'react-native-svg'

const COPIED_TIMEOUT = 4000

/**
 * A bite out of the card: a disc the colour of what the card is lying on, half of it clipped away
 * by the card's own edge. On a white sheet that is white, where on the pink page it was the
 * ground.
 */
const NOTCH_SIZE = 18
const NOTCH = {
	position: 'absolute' as const,
	left: -NOTCH_SIZE / 2,
	width: NOTCH_SIZE,
	height: NOTCH_SIZE,
	borderRadius: NOTCH_SIZE / 2,
	backgroundColor: colors.white,
}

/**
 * The line the card tears along, bitten at both ends.
 *
 * Drawn as a stroke and not a dashed border: iOS declines to dash a border applied to one side
 * only, so `borderWidth` with `borderStyle: 'dashed'` drew nothing at all and the notches sat
 * there with no line between them.
 *
 * It runs down the card rather than across it, which is where a ticket actually tears — and the
 * halves it makes are the two halves of the offer.
 */
const Perforation = () => (
	<View className="relative w-px">
		<Svg width={1} style={{ flex: 1 }}>
			<Line
				x1="0.5"
				y1="0"
				x2="0.5"
				y2="100%"
				stroke={colors.edge}
				strokeWidth={1}
				strokeDasharray="6 5"
			/>
		</Svg>
		<View style={[NOTCH, { top: -NOTCH_SIZE / 2 }]} />
		<View style={[NOTCH, { bottom: -NOTCH_SIZE / 2 }]} />
	</View>
)

/**
 * One side of the offer: the mark in the corner, who it is for, and what they get.
 *
 * The figure is the display face at its smallest, which is still the largest thing on the sheet
 * — a referral is two amounts, and the amounts are the offer.
 */
const Half = ({
	glyph,
	label,
	amount,
	line,
}: {
	glyph: 'sparkle' | 'dollar'
	label: string
	amount: string | null
	line: string
}) => (
	<VStack gap={14} className="flex-1 p-4">
		{/* Mark and label on one line, at the disc's own size and gap — the pairing the score
		    card and the membership card both open with. Stacked, the label was a second row of
		    its own and the half had three things above the figure instead of one. */}
		<HStack className="items-center" gap={10}>
			<View className="h-10 w-10 items-center justify-center rounded-full bg-white">
				<Glyph name={glyph} size={20} color={colors.ink} />
			</View>
			<Caps>{label}</Caps>
		</HStack>
		<VStack gap={4}>
			{/* Its own leading. The display face is set at a line the height of its own type,
			    which is right for a row of numerals and clips the dollar's ascender, since the
			    sign stands taller than the figures beside it. */}
			<Header
				variant="display-sm"
				numberOfLines={1}
				adjustsFontSizeToFit
				className="leading-[66px]"
			>
				{amount ?? '—'}
			</Header>
			<Text size="body2" tone="soft">
				{line}
			</Text>
		</VStack>
	</VStack>
)

const Caps = ({ children }: { children: string }) => (
	<Text size="body3" tone="soft" className="uppercase tracking-[0.08em]">
		{children}
	</Text>
)

/**
 * Give one, get one, in a slide-up.
 *
 * A referral is a thing you hand somebody, so it is drawn as one: the offer above a tear, the
 * code below it, on a single card. The card is blush here rather than white — the sheet it sits
 * on is already white, and a white card on a white sheet is not a card.
 *
 * It is a sheet rather than a page because the whole of it is one short offer and one action.
 * The page it replaced opened with the app's only H1 and then said the same amount again in a
 * paragraph under it, which is what a screen does when it has been given more room than it has
 * to say.
 */
export const ReferSheet = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
	const [isCopied, setIsCopied] = useState(false)
	const { referralUrl, discountInCents, isLoading, hasRenderableReferral } =
		useFetchPatientReferral()

	const amount = discountInCents !== undefined ? formatCents(discountInCents) : null

	/** The web has no share sheet, so there the one button copies the link instead of sending it. */
	const copyLink = async () => {
		await Clipboard.setStringAsync(referralUrl)
		setIsCopied(true)
		setTimeout(() => setIsCopied(false), COPIED_TIMEOUT)
	}

	const share = async () => {
		analytics.trackClick('home-page-referral')
		if (Platform.OS === 'android' || Platform.OS === 'ios') {
			await Share.share({
				// What the recipient reads, so it says the offer rather than "check this out".
				message: amount
					? `Join Wally and get ${amount} off your first year: ${referralUrl}`
					: `Join Wally: ${referralUrl}`,
			})
		} else {
			await copyLink()
		}
	}

	/**
	 * The sequence, and nothing else.
	 *
	 * What a member cannot get from the card above is the order, and the numbering carries it:
	 * once the second step says the friend joins, the third does not have to say "when they do"
	 * to place itself after it.
	 */
	const steps = [
		'Share your link',
		amount ? `They join Wally and get ${amount} off` : 'They join Wally',
		amount ? `You get a ${amount} Amazon gift card` : 'You get yours',
	]

	return (
		<Sheet open={open} onClose={onClose}>
			<View className="pt-2">
				<SheetTitle glyph="heart">Refer a Friend</SheetTitle>

				<VStack gap={20} className="px-4 pb-8">
					<Skeleton isLoading={isLoading} className="h-40 w-full rounded-note bg-blush">
						{hasRenderableReferral ? (
							<View className="rounded-note flex-row overflow-hidden bg-blush">
								{/* The tear divides the offer, because that is the thing here with two
								    halves. It used to divide the offer from the code, which made a
								    fallback the equal of the thing being offered. */}
								<Half
									glyph="sparkle"
									label="They get"
									amount={amount}
									line="Off their first year"
								/>
								<Perforation />
								<Half
									glyph="dollar"
									label="You get"
									amount={amount}
									line="Amazon gift card"
								/>
							</View>
						) : (
							<VStack gap={10}>
								<Header variant="h3">Referrals aren&rsquo;t set up yet</Header>
								<Text size="body1" tone="soft">
									We couldn&rsquo;t find a referral link for your account. Check back
									soon.
								</Text>
							</VStack>
						)}
					</Skeleton>

					{hasRenderableReferral && (
						<>
							{/* Straight onto the sheet's white, as the membership sheet sets its own
							    ledger: a card inside a card would be a third surface for three lines.
							    Set further down than the sheet's own rhythm, because the offer above it
							    is the subject and this is the note on it. */}
							<VStack gap={14} className="mt-4">
								<Caps>How it works</Caps>
								<Ledger items={steps} mark={numbered} />
							</VStack>

							{/* At the foot, where a sheet's one action sits, and black rather than coral:
							    the coupon is already the loudest thing here, and coral is meant to be
							    the one thing on a screen that has to be noticed. */}
							<Button size="lg" fullWidth onPress={share}>
								<ButtonText>
									{Platform.OS === 'web'
										? isCopied
											? 'Copied'
											: 'Copy Link'
										: 'Share Invite'}
								</ButtonText>
							</Button>
						</>
					)}
				</VStack>
			</View>
		</Sheet>
	)
}
